/**
 * useTodoPreferences - Manages sort/filter preferences with AsyncStorage persistence.
 */

import { useCallback, useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type {
  TodoSortOption,
  TodoFilterOption,
  TodoPreferences,
} from '@/types/todo-preferences';
import { DEFAULT_TODO_PREFERENCES } from '@/types/todo-preferences';

const STORAGE_KEY = 'todo_preferences';

type UseTodoPreferencesResult = {
  preferences: TodoPreferences;
  setSort: (sort: TodoSortOption) => void;
  setFilter: (filter: TodoFilterOption) => void;
  isDragEnabled: boolean;
  isLoaded: boolean;
};

export function useTodoPreferences(): UseTodoPreferencesResult {
  const [preferences, setPreferences] = useState<TodoPreferences>(DEFAULT_TODO_PREFERENCES);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const stored = await AsyncStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as Partial<TodoPreferences>;
          setPreferences({
            sort: parsed.sort ?? DEFAULT_TODO_PREFERENCES.sort,
            filter: parsed.filter ?? DEFAULT_TODO_PREFERENCES.filter,
          });
        }
      } catch (error) {
        console.error('[useTodoPreferences] Failed to load preferences:', error);
      } finally {
        setIsLoaded(true);
      }
    };
    loadPreferences();
  }, []);

  const persistPreferences = useCallback(async (newPrefs: TodoPreferences) => {
    try {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(newPrefs));
    } catch (error) {
      console.error('[useTodoPreferences] Failed to persist preferences:', error);
    }
  }, []);

  const setSort = useCallback((sort: TodoSortOption) => {
    setPreferences(prev => {
      const newPrefs = { ...prev, sort };
      persistPreferences(newPrefs);
      return newPrefs;
    });
  }, [persistPreferences]);

  const setFilter = useCallback((filter: TodoFilterOption) => {
    setPreferences(prev => {
      const newPrefs = { ...prev, filter };
      persistPreferences(newPrefs);
      return newPrefs;
    });
  }, [persistPreferences]);

  // Drag is only enabled when sorting manually
  const isDragEnabled = preferences.sort === 'manual';

  return {
    preferences,
    setSort,
    setFilter,
    isDragEnabled,
    isLoaded,
  };
}
